import { useState, useCallback, useMemo } from "react";
import { Box } from "@mui/material";
import type { ChatTokenData } from "Api/Queries/Chat/useChatTokenQuery";
import { useChatChannelSync } from "Hooks/Chat/useChatChannelSync";
import { useOpenCreatedChannel } from "Hooks/Chat/useOpenCreatedChannel";
import { useChatChannelFilters } from "Hooks/Chat/useChatChannelFilters";
import { ChatChannelListPanel } from "Components/Chat/ChatChannelListPanel";
import { ChatMessagePanel } from "Components/Chat/ChatMessagePanel";
import { NewDirectChatDialog } from "Components/Chat/NewDirectChatDialog";
import { NewGroupChatDialog } from "Components/Chat/NewGroupChatDialog";
import { ChatCategoryProvider } from "Components/Chat/ChatCategoryContext";
import { useNavigate } from "Navigation/useNavigate";
import { appUrl } from "Utils/UrlUtils";
import { usePendingChatRequestCount } from "Hooks/Chat/usePendingChatRequestCount";
import { useStreamClientNotifications } from "Hooks/Chat/useStreamClientNotifications";
import type { ChatChannelCategory } from "Components/Chat/chatChannelCategories";

type Props = {
  isMobile: boolean;
  chatTokenData: ChatTokenData;
  onDetailViewChange: (isDetail: boolean) => void;
};

export const ChatInner = ({
  isMobile,
  chatTokenData: { userId },
  onDetailViewChange,
}: Props) => {
  const [isNewChatDialogOpen, setIsNewChatDialogOpen] = useState(false);
  const [isNewGroupDialogOpen, setIsNewGroupDialogOpen] = useState(false);
  const navigate = useNavigate();
  const handleChannelCreated = useOpenCreatedChannel();
  useStreamClientNotifications();

  const { activeChannel, isChannelOpen, handleBackToList, setActiveChannel } =
    useChatChannelSync({
      isMobile,
      onDetailViewChange,
    });

  const {
    filters,
    searchText,
    isSearching,
    selectedCategory,
    setSearchText,
    setSelectedCategory,
    channelRenderFilterFn,
    isChannelFilterPending,
    isShowingRejected,
    setIsShowingRejected,
  } = useChatChannelFilters(userId);

  const pendingRequestCount = usePendingChatRequestCount();

  const handleCategoryChange = useCallback(
    (category: ChatChannelCategory) => {
      if (!isMobile) {
        setActiveChannel(undefined);
        navigate(appUrl("chat"), { replace: true });
      }
      setSelectedCategory(category);
    },
    [isMobile, setActiveChannel, navigate, setSelectedCategory],
  );

  const categoryContextValue = useMemo(
    () => ({ setSelectedCategory: handleCategoryChange }),
    [handleCategoryChange],
  );

  return (
    <ChatCategoryProvider value={categoryContextValue}>
      <Box display="flex" height="100%" width="100%" position="relative">
        <ChatChannelListPanel
          isMobile={isMobile}
          filters={filters}
          searchText={searchText}
          isChannelOpen={isChannelOpen}
          selectedCategory={selectedCategory}
          onSearchChange={setSearchText}
          isSearching={isSearching}
          onCategoryChange={handleCategoryChange}
          channelRenderFilterFn={channelRenderFilterFn}
          isChannelFilterPending={isChannelFilterPending}
          onNewChatClick={() => setIsNewChatDialogOpen(true)}
          onNewGroupChatClick={() => setIsNewGroupDialogOpen(true)}
          pendingRequestCount={pendingRequestCount}
          isShowingRejected={isShowingRejected}
          onToggleRejected={setIsShowingRejected}
        />
        <ChatMessagePanel
          isMobile={isMobile}
          isChannelOpen={isChannelOpen}
          onBack={handleBackToList}
          activeChannel={activeChannel}
        />
      </Box>
      <NewDirectChatDialog
        isOpen={isNewChatDialogOpen}
        onClose={() => setIsNewChatDialogOpen(false)}
        onChannelCreated={handleChannelCreated}
      />
      <NewGroupChatDialog
        isOpen={isNewGroupDialogOpen}
        onClose={() => setIsNewGroupDialogOpen(false)}
        onChannelCreated={handleChannelCreated}
      />
    </ChatCategoryProvider>
  );
};
